import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SleepTracker = ({ promptId, onComplete }) => {
  const [sleepData, setSleepData] = useState({
    hours_slept: '',
    sleep_quality: 'good',
    bed_time: '',
    wake_time: '',
    notes: ''
  });
  const [todaySleep, setTodaySleep] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTodaySleep();
  }, []);

  const loadTodaySleep = async () => {
    try {
      const response = await axios.get(`${API}/sleep/today`);
      if (response.data.logged !== false) {
        setTodaySleep(response.data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to load sleep data');
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!sleepData.hours_slept || sleepData.hours_slept <= 0) {
      toast.error('Please enter valid sleep hours');
      return;
    }

    try {
      const response = await axios.post(`${API}/sleep/log`, {
        hours_slept: parseFloat(sleepData.hours_slept),
        sleep_quality: sleepData.sleep_quality,
        bed_time: sleepData.bed_time || null,
        wake_time: sleepData.wake_time || null,
        notes: sleepData.notes || null
      });

      toast.success(`Sleep logged! +${response.data.points_earned} points earned!`);
      
      // Handle badge notifications
      if (response.data.new_badges && response.data.new_badges.length > 0) {
        response.data.new_badges.forEach((badge, index) => {
          setTimeout(() => {
            toast.success(`🏆 Badge Earned: ${badge}!`, { duration: 5000 });
          }, 1000 + (index * 1000));
        });
      }
      
      // Handle level up
      if (response.data.level_increased) {
        setTimeout(() => {
          toast.success('🎊 Level Up! You\'re getting stronger!', { duration: 5000 });
        }, 500);
      }

      loadTodaySleep();
      
      // Mark prompt as complete
      if (onComplete) {
        await onComplete(promptId, 
          `Logged ${sleepData.hours_slept} hours of ${sleepData.sleep_quality} sleep`, 
          'sleep'
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log sleep');
    }
  };

  if (loading) {
    return (
      <Card className="bg-blue-900/70 border border-blue-700">
        <CardContent className="py-8 text-center">
          <div className="animate-pulse text-yellow-400">Loading sleep tracker...</div>
        </CardContent>
      </Card>
    );
  }

  if (todaySleep) {
    return (
      <Card className="bg-blue-900/70 border border-blue-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-lg">Daily Sleep Log</CardTitle>
          <p className="text-gray-300 text-sm">Track your sleep and recovery</p>
          
          {/* Sleep Tips */}
          <div className="mt-3 p-3 bg-purple-900/50 rounded-lg border border-purple-400/40">
            <h4 className="text-purple-400 font-semibold mb-2 text-sm">💤 Sleep Tips</h4>
            <div className="text-xs text-gray-300 space-y-1">
              <p>• Aim for 7-9 hours of sleep for optimal athletic recovery</p>
              <p>• Maintain consistent sleep and wake times</p>
              <p>• Avoid screens 1 hour before bedtime</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-green-900/50 border border-green-500/40 rounded-lg p-4">
            <h3 className="text-green-400 font-semibold mb-3">✅ Sleep Logged for Today</h3>
            <div className="space-y-2 text-white">
              <div className="flex justify-between">
                <span className="text-gray-300">Hours Slept:</span>
                <span className="font-bold">{todaySleep.hours_slept} hours</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-300">Quality:</span>
                <span className="font-bold capitalize">{todaySleep.sleep_quality}</span>
              </div>
              {todaySleep.bed_time && (
                <div className="flex justify-between">
                  <span className="text-gray-300">Bed Time:</span>
                  <span className="font-bold">{todaySleep.bed_time}</span>
                </div>
              )}
              {todaySleep.wake_time && (
                <div className="flex justify-between">
                  <span className="text-gray-300">Wake Time:</span>
                  <span className="font-bold">{todaySleep.wake_time}</span>
                </div>
              )}
              {todaySleep.notes && (
                <div className="mt-3 pt-3 border-t border-green-500/30">
                  <p className="text-gray-300 text-sm"><span className="font-medium">Notes:</span> {todaySleep.notes}</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-blue-900/70 border border-blue-700">
      <CardHeader className="pb-3">
        <CardTitle className="text-white text-lg">Daily Sleep Log</CardTitle>
        <p className="text-gray-300 text-sm">Track your sleep and recovery</p>
        
        {/* Sleep Tips */}
        <div className="mt-3 p-3 bg-purple-900/50 rounded-lg border border-purple-400/40">
          <h4 className="text-purple-400 font-semibold mb-2 text-sm">💤 Sleep Tips</h4>
          <div className="text-xs text-gray-300 space-y-1">
            <p>• Aim for 7-9 hours of sleep for optimal athletic recovery</p>
            <p>• Maintain consistent sleep and wake times</p>
            <p>• Avoid screens 1 hour before bedtime</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Hours Slept */}
        <div className="space-y-2">
          <Label htmlFor="hours_slept" className="text-white">
            Hours Slept Last Night <span className="text-red-400">*</span>
          </Label>
          <Input
            id="hours_slept"
            type="number"
            step="0.5"
            min="0"
            max="24"
            value={sleepData.hours_slept}
            onChange={(e) => setSleepData({...sleepData, hours_slept: e.target.value})}
            placeholder="e.g., 8"
            className="bg-blue-900/50 text-white placeholder:text-gray-400 border-blue-700"
          />
        </div>

        {/* Sleep Quality */}
        <div className="space-y-2">
          <Label htmlFor="sleep_quality" className="text-white">Sleep Quality</Label>
          <select
            id="sleep_quality"
            value={sleepData.sleep_quality}
            onChange={(e) => setSleepData({...sleepData, sleep_quality: e.target.value})}
            className="w-full bg-blue-900/50 text-white border border-blue-700 rounded-md p-2"
          >
            <option value="excellent">😴 Excellent - Woke up refreshed</option>
            <option value="good">🙂 Good - Felt rested</option>
            <option value="fair">😐 Fair - Somewhat rested</option>
            <option value="poor">😞 Poor - Still tired</option>
          </select>
        </div>

        {/* Optional Times */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="bed_time" className="text-white text-sm">Bed Time (Optional)</Label>
            <Input
              id="bed_time"
              type="time"
              value={sleepData.bed_time}
              onChange={(e) => setSleepData({...sleepData, bed_time: e.target.value})}
              className="bg-blue-900/50 text-white border-blue-700"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="wake_time" className="text-white text-sm">Wake Time (Optional)</Label>
            <Input
              id="wake_time"
              type="time"
              value={sleepData.wake_time}
              onChange={(e) => setSleepData({...sleepData, wake_time: e.target.value})}
              className="bg-blue-900/50 text-white border-blue-700"
            />
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <Label htmlFor="notes" className="text-white text-sm">Notes (Optional)</Label>
          <textarea
            id="notes"
            value={sleepData.notes}
            onChange={(e) => setSleepData({...sleepData, notes: e.target.value})}
            placeholder="How did you feel? Any disturbances?"
            rows={2}
            className="w-full bg-blue-900/50 text-white placeholder:text-gray-400 border-blue-700 rounded-md p-2"
          />
        </div>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          className="w-full bg-gradient-to-r from-blue-900 to-purple-600 hover:from-blue-800 hover:to-purple-700"
        >
          Log Sleep & Earn Points
        </Button>
      </CardContent>
    </Card>
  );
};

export default SleepTracker;
